/* The operations screens: the Stock Admin queue and the one check (SA1, SA2) and the approvals
   (SL7, X2, X3, R3). Tokens only; every target is at least 44 px; nothing scrolls the page
   sideways (wide tables scroll inside their cards). */

.stack {
  display: flex;
  flex-direction: column;
  gap: 28px;
  min-width: 0;
}

/* --- Text ------------------------------------------------------------------------------------------- */

.strong {
  color: var(--text-heading);
  font-weight: var(--fw-semibold);
}

.muted {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.sub {
  display: block;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.45;
}

/* A short value that shouldn't break ("10 days") above a sub line that may. */
.nowrap {
  display: block;
  white-space: nowrap;
}

.cardText {
  margin: 0;
  color: var(--text-secondary);
  font-size: 15px;
  line-height: 1.6;
}

.subhead {
  margin: 8px 0 0;
  color: var(--text-heading);
  font-size: 15px;
  font-weight: var(--fw-bold);
}

.eyebrowMuted {
  margin: 0;
  color: var(--text-secondary);
}

.fieldLabel {
  display: block;
  margin: 0 0 4px;
  color: var(--text-secondary);
}

.code {
  margin: 0;
  color: var(--text-heading);
  font-family: var(--font-mono);
  font-size: 14px;
  overflow-wrap: anywhere;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.negative {
  color: var(--status-problem-ink);
}

.struck {
  text-decoration: line-through;
}

.strongLink,
.inlineLink {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--text-link);
  font-weight: var(--fw-semibold);
  text-decoration: none;
}

.inlineLink {
  min-height: 44px;
  text-decoration: underline;
}

.strongLink:hover,
.strongLink[data-hovered],
.inlineLink:hover,
.inlineLink[data-hovered] {
  color: var(--text-link-hover-on-light);
  text-decoration: underline;
}

.strongLink[data-focus-visible],
.inlineLink[data-focus-visible],
.back[data-focus-visible],
.onDarkLink[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.back {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  align-self: flex-start;
  min-height: 44px;
  color: var(--text-link);
  font-family: var(--font-ui);
  font-size: 13px;
  letter-spacing: 0.1em;
  text-decoration: none;
  text-transform: uppercase;
}

.back:hover,
.back[data-hovered] {
  color: var(--text-link-hover-on-light);
  text-decoration: underline;
}

/* --- Notices ----------------------------------------------------------------------------------------- */

.notice {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid var(--info-bg);
  border-radius: var(--radius-sm);
  background: var(--brand-cyan-50);
  color: var(--status-info-ink);
  font-size: 14px;
  line-height: 1.5;
}

.notice > svg {
  flex: none;
  margin-top: 3px;
}

.noticeProblem {
  border-color: var(--status-problem-bg);
  background: var(--status-problem-bg);
  color: var(--status-problem-ink);
}

.noticeDone {
  border-color: var(--status-done-bg);
  background: var(--status-done-bg);
  color: var(--status-done-ink);
}

.noticeWaiting {
  border-color: var(--border-urgent);
  background: var(--surface-urgent);
  color: var(--status-waiting-ink);
}

/* APP-148: "You'd be acting for an Executive." under a Stock Admin's buttons (Overage board). */
.actingNote > svg {
  margin-top: 1px;
}

/* --- People ------------------------------------------------------------------------------------------- */

.personChip {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  min-width: 0;
}

.avatar {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-circle);
  background: var(--brand-sky);
  color: var(--brand-navy-950);
  font-family: var(--font-ui);
  font-size: 12px;
}

.avatarCyan {
  background: var(--brand-cyan);
}

.avatarLg {
  width: 52px;
  height: 52px;
  background: var(--brand-navy);
  color: var(--white);
  font-size: 16px;
}

.personText {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.personName {
  color: inherit;
  font-weight: var(--fw-semibold);
}

.personDetail {
  color: inherit;
  font-size: 12px;
  opacity: 0.85;
}

.personHead {
  display: flex;
  gap: 14px;
  align-items: center;
  color: var(--text-heading);
}

.personHead .personDetail {
  color: var(--text-secondary);
  opacity: 1;
}

/* --- Queue: steps, tabs, filters ---------------------------------------------------------------------- */

.steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.stepsHead {
  display: flex;
  gap: 8px;
  align-items: center;
}

.stepsTitle {
  margin: 0;
  color: var(--text-heading);
}

/* The eight steps in even rows (APP-067 §13): four by four beside the views, two by two on a
   phone, each tile as tall as its row. */
.stepList {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

@media (min-width: 720px) {
  .stepList {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.stepList > li {
  display: flex;
}

.stepTile {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 4px;
  min-height: 112px;
  padding: 14px 14px 12px;
  border: 1px solid var(--border-card);
  border-top: 3px solid transparent;
  border-radius: var(--radius-md);
  background: var(--surface-card);
  color: var(--text-heading);
  text-decoration: none;
}

.stepTile:hover,
.stepTile[data-hovered] {
  border-color: var(--brand-cyan);
}

.stepTile[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.stepTile[aria-current='true'] {
  border-top-color: var(--brand-cyan);
  background: var(--brand-cyan-50);
}

.stepProblem {
  border-top-color: var(--status-problem-ink);
}

.stepLabel {
  color: var(--text-secondary);
}

.stepProblem .stepLabel {
  color: var(--status-problem-ink);
}

.stepCount {
  font-size: 32px;
  line-height: 1;
}

.stepNote {
  color: var(--text-secondary);
  font-size: 12px;
}

.filter:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.filterCount {
  display: inline-grid;
  place-items: center;
  min-width: 24px;
  height: 22px;
  padding: 0 7px;
  border-radius: var(--radius-pill);
  background: var(--gray-100);
  color: var(--brand-navy);
  font-size: 11px;
  letter-spacing: 0;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 0;
}

.filter {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-pill);
  color: var(--text-heading);
  font-size: 13px;
  text-decoration: none;
}

.filter[aria-current='page'],
.filter[aria-pressed='true'] {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
  color: var(--white);
}

.filterNote {
  color: var(--text-secondary);
  font-size: 13px;
}

.ownList,
.teamList,
.alsoList,
.approvalList,
.poolList,
.paidFrom,
.checklist,
.files,
.codingList {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ownList,
.alsoList {
  gap: 8px;
}

.ownRow {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  align-items: baseline;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.teamList {
  gap: 14px;
}

.onDarkNote {
  margin: 0;
  color: var(--text-on-dark-muted);
  font-size: 13px;
  line-height: 1.5;
}

/* White and underlined on the navy card: cyan text there falls below 4.5:1. */
.onDarkLink {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--white);
  font-weight: var(--fw-semibold);
  text-decoration: underline;
  text-decoration-color: var(--brand-cyan);
  text-underline-offset: 3px;
}

/* --- Tiles (amounts, ledger) ------------------------------------------------------------------------ */

.tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
  gap: 12px;
}

.tile {
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
}

.tileCyan {
  border-color: var(--info-bg);
  background: var(--brand-cyan-50);
}

.tileGold {
  border-color: var(--border-urgent);
  background: var(--surface-urgent);
}

.tileProblem {
  border-color: var(--status-problem-bg);
  background: var(--status-problem-bg);
}

.tileDone {
  border-color: var(--status-done-bg);
  background: var(--status-done-bg);
}

.tileLabel {
  margin: 0 0 6px;
  color: var(--text-secondary);
  overflow-wrap: anywhere;
}

.tileProblem .tileLabel,
.tileProblem .tileValue {
  color: var(--status-problem-ink);
}

.tileDone .tileLabel,
.tileDone .tileValue {
  color: var(--status-done-ink);
}

.tileGold .tileLabel,
.tileGold .tileValue {
  color: var(--text-gold);
}

.tileValue {
  margin: 0;
  color: var(--text-heading);
  font-size: 28px;
  line-height: 1.1;
  overflow-wrap: anywhere;
}

.tileNote {
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 12px;
}

.bars {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 8px 14px;
  align-items: center;
}

.barLabel {
  color: var(--text-secondary);
  font-size: 13px;
}

.bar {
  display: flex;
  height: 14px;
  overflow: hidden;
  border-radius: var(--radius-sm);
  background: var(--gray-100);
}

.barNavy {
  background: var(--brand-navy);
}

.barGreen {
  background: var(--status-done-ink);
}

.barGold {
  background: var(--brand-gold);
}

/* --- Line, FMV, paid from ------------------------------------------------------------------------------ */

.split {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  gap: 20px 28px;
  padding-top: 16px;
  border-top: 1px solid var(--border-hairline);
}

.split > div {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.split p {
  margin: 0;
}

.paidFrom {
  gap: 6px;
}

.paidRow {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-hairline);
  font-size: 14px;
}

.paidBox {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
}

.paidBox p {
  margin: 0;
}

.totalRow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  justify-content: space-between;
  margin: 0;
  padding-top: 12px;
  border-top: 2px solid var(--brand-navy);
  color: var(--text-heading);
  font-size: 15px;
}

/* --- Coding -------------------------------------------------------------------------------------------- */

.codingList {
  gap: 0;
}

/* Who · cost type · codes, wrapping onto new lines as the card narrows. The codes get the most room
   so a code never breaks in the middle (15125.110 stays on one line). */
.codingRow {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  align-items: flex-start;
  padding: 14px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.codingWho,
.codingType {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.codingWho {
  flex: 1 1 180px;
}

.codingType {
  flex: 0 1 150px;
}

.codes {
  display: grid;
  flex: 2 1 300px;
  grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
  gap: 12px;
  min-width: 0;
  margin: 0;
}

.codes dd {
  margin: 0;
  overflow-wrap: normal;
}

/* The 1px gap shows the border colour between the choices, side by side or wrapped onto rows; a
   wrapped choice stretches to fill its row. */
.segmented {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 1px;
  align-self: flex-start;
  overflow: hidden;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--border-control);
}

.segment {
  flex: 1 1 auto;
  min-width: 64px;
  min-height: 44px;
  padding: 0 14px;
  border: 0;
  background: var(--white);
  color: var(--brand-navy);
  font-family: var(--font-ui);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
}

.segment[data-selected] {
  background: var(--brand-navy);
  color: var(--white);
}

.segment[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -3px;
}

.segment[data-disabled] {
  cursor: default;
  opacity: 0.6;
}

/* --- Files ---------------------------------------------------------------------------------------------- */

.files {
  gap: 10px;
}

.file {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  align-items: center;
  padding: 12px 14px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
}

.fileIcon {
  display: grid;
  flex: none;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-sm);
  background: var(--brand-cyan-50);
  color: var(--status-info-ink);
}

.fileText {
  display: flex;
  flex: 1 1 220px;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.fileName {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  color: var(--text-heading);
  font-weight: var(--fw-semibold);
  overflow-wrap: anywhere;
}

a.fileName {
  min-height: 44px;
  color: var(--text-link);
}

a.fileName:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.fileMeta {
  color: var(--text-secondary);
  font-size: 12px;
}

.fileActions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.attach {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}

/* The boards' upload control (`.drop`): white, a dashed grey border, navy words; cyan on hover.
   Inside `.attach`, so it wins over the shared Button's ghost look whatever the CSS order. */
.attach .attachButton {
  border: 1.5px dashed var(--gray-500);
  background: var(--white);
  color: var(--brand-navy);
}

.attach .attachButton:hover,
.attach .attachButton[data-hovered] {
  border-color: var(--brand-cyan);
  background: var(--brand-cyan-50);
  color: var(--brand-navy);
}

.attachRow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  align-items: center;
}

.drop {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  align-items: center;
  justify-content: center;
  padding: 22px;
  border: 2px dashed var(--border-dashed);
  border-radius: var(--radius-md);
  color: var(--status-info-ink);
}

.drop[data-drop-target] {
  border-color: var(--brand-cyan);
  background: var(--brand-cyan-50);
}

.drop[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.dropText {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.quote {
  margin: 0;
  padding: 14px 16px;
  border-left: 3px solid var(--brand-cyan);
  background: var(--surface-soft);
  color: var(--text-body);
  font-size: 15px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.quoteText {
  margin: 0;
}

/* --- Tax -------------------------------------------------------------------------------------------------- */

.taxLine {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.tableScroll {
  position: relative;
  max-width: 100%;
  overflow-x: auto;
}

.tableScroll:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.taxTable {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 14px;
}

.taxTable th,
.taxTable td {
  padding: 10px;
  border-bottom: 1px solid var(--border-hairline);
  text-align: left;
  vertical-align: middle;
}

.taxTable thead th {
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: var(--fw-medium);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
}

.taxTable .num {
  text-align: right;
}

.choice {
  color: var(--text-heading);
  font-weight: var(--fw-semibold);
}

.adp {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border: 1px dashed var(--border-dashed);
  border-radius: var(--radius-sm);
}

.adp p {
  margin: 0;
}

/* --- Inputs in rows ---------------------------------------------------------------------------------------- */

.amount {
  display: inline-flex;
  min-width: 0;
}

.amountInput {
  width: 9rem;
  max-width: 100%;
  min-height: var(--control-height);
  padding: 0 10px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--text-body);
  font: inherit;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  text-align: right;
}

.amountInput[data-focused],
.amountInput:focus {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.amountInput[data-invalid] {
  border-color: var(--status-problem-ink);
}

.amountInput[data-disabled] {
  background: var(--gray-50);
  color: var(--text-muted);
}

.checkbox {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  min-height: 44px;
  color: var(--text-heading);
  font-size: 14px;
  cursor: pointer;
}

.checkbox[data-disabled] {
  cursor: default;
}

.box {
  display: grid;
  flex: none;
  place-items: center;
  width: 22px;
  height: 22px;
  border: 2px solid var(--gray-450);
  border-radius: 4px;
  background: var(--white);
  color: var(--white);
}

.boxOn {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
}

.checkbox[data-focus-visible] .box {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.attest {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 12px 0 0;
  border: 0;
  border-top: 1px solid var(--border-hairline);
}

.attest legend {
  padding: 0 0 4px;
}

.formRow {
  composes: field-row from global;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr));
  gap: 16px;
}

.inlineField {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fieldName {
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.actionsRow {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.saveBar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
}

/* --- Checklist and claim ----------------------------------------------------------------------------------- */

.checklist {
  gap: 0;
}

.checkItem {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.checkMark {
  display: grid;
  flex: none;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: var(--radius-circle);
  background: var(--gray-100);
  color: var(--brand-navy);
}

.checkDone {
  background: var(--status-done-bg);
  color: var(--status-done-ink);
}

.checkOpen {
  background: var(--status-waiting-bg);
  color: var(--status-waiting-ink);
}

/* Before a claim: numbered and unticked, so nothing reads as already checked (QA-015). */
.checkNumber {
  border: 1px solid var(--border-control);
  background: var(--white);
  font-size: 12px;
  font-weight: var(--fw-semibold);
}

.checkText {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.claimPanel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* A button and the line that says who it affects (QA-037). */
.buttonWithNote {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* --- About and summary ------------------------------------------------------------------------------------- */

.about,
.summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
}

.aboutRow,
.summaryRow {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  justify-content: space-between;
  font-size: 14px;
}

.aboutRow dt {
  color: var(--text-on-dark-muted);
}

.aboutRow dd {
  margin: 0;
  color: var(--white);
  font-weight: var(--fw-semibold);
  text-align: right;
}

.summaryRow dt {
  color: var(--text-secondary);
}

.summaryRow dd {
  margin: 0;
  text-align: right;
}

.poolAfter {
  padding-top: 12px;
  border-top: 1px solid var(--border-hairline);
}

.poolAfter p {
  margin: 0;
}

.bigValue {
  color: var(--text-heading);
  font-size: 30px;
}

/* --- Approvals ---------------------------------------------------------------------------------------------- */

.approvalList {
  gap: 0;
}

.approvalItem {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.approvalHead {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  align-items: center;
}

.pair {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
  gap: 28px;
  align-items: start;
}

.facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr));
  gap: 12px 20px;
  margin: 0;
}

.factValue {
  margin: 0;
  color: var(--text-heading);
  font-size: 16px;
  font-weight: var(--fw-semibold);
}

.poolList {
  gap: 0;
}

.poolRow {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--border-hairline);
}

.poolName {
  flex: 1 1 100%;
  color: var(--text-heading);
  font-size: 14px;
}

.poolMove {
  margin: 0;
  color: var(--text-secondary);
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}

/* The arrow between an amount before and after (ThenArrow), drawn so it's seen but never part of
   the text; screen readers and a copy get the hidden " then " beside it instead. */
.thenArrow::before {
  content: ' → ';
  white-space: pre;
}

.topUps {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.topUp {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  border: 0;
}

.topUp legend {
  padding: 0 0 6px;
}

.topUpFields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.rules {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 12px 0 0 18px;
  border-top: 1px solid var(--border-hairline);
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

.steps2 {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding-left: 20px;
  color: var(--text-on-dark-soft);
  font-size: 14px;
  line-height: 1.5;
}

.chipGroup {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.chipRadio {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-pill);
  background: var(--white);
  color: var(--brand-navy);
  font-weight: var(--fw-semibold);
  cursor: pointer;
}

.chipRadio[data-selected] {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
  color: var(--white);
}

.chipRadio[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.chipRadio[data-disabled] {
  cursor: default;
  opacity: 0.6;
}

/* SL7 "Last 12 months": the awards, then the repeat pill when there is one (QA-006). */
.recentCell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  align-items: center;
}

/* --- The Letters view's facts (SA3 day one) ---------------------------------------------------------------- */

.letterTypes,
.templateVersions,
.recipients {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.letterTypes {
  gap: 14px;
  margin-bottom: 12px;
}

.letterType,
.recipientRow {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.recipients {
  gap: 12px;
}

.groupLabel {
  color: var(--text-muted);
  font-size: 12px;
  font-weight: var(--fw-semibold);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.templateVersion {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-subtle);
}

.templateVersionText {
  display: flex;
  flex: 1 1 260px;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

/* --- SA1's Today: time, a dot, then what happened ------------------------------------------------------- */

.today {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.todayRow {
  display: grid;
  grid-template-columns: minmax(84px, auto) 10px minmax(0, 1fr);
  gap: 4px 12px;
  align-items: baseline;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-subtle);
}

.todayTime {
  color: var(--text-secondary);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Decoration: the line's words say what happened, never the colour alone. */
.todayDot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--gray-500);
}

.dotClaim {
  background: var(--brand-navy);
}

.dotApproval {
  background: var(--brand-cyan);
}

.dotPayroll {
  background: var(--status-done-ink);
}

.todayText {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.todayLink {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: var(--text-heading);
  font-weight: var(--fw-semibold);
  text-decoration: none;
  overflow-wrap: anywhere;
}

.todayLink:hover,
.todayLink[data-hovered] {
  color: var(--text-link-hover-on-light);
  text-decoration: underline;
}

.todayLink[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

@media (max-width: 719.98px) {
  .todayRow {
    grid-template-columns: 10px minmax(0, 1fr);
  }

  .todayTime {
    grid-column: 2;
  }

  .todayDot {
    grid-row: 2;
    grid-column: 1;
  }

  .todayText {
    grid-column: 2;
  }
}

/* A link inside a line of small text ("· History"): a 44 px box around the word, with negative
   margins so the line keeps its height (as the shared inline History, QA-043). */
.lineLink {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  margin-block: -12px;
  margin-block: calc((1lh - 44px) / 2);
  color: var(--text-link);
  text-decoration: underline;
}

.lineLink:hover,
.lineLink[data-hovered] {
  color: var(--text-link-hover-on-light);
}

.lineLink[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -8px;
}

/* Executive's decisions by kind, on the right of Level 2 (X2, X3): a two- or three-part switch. */
.kindSwitch {
  display: inline-flex;
  flex-wrap: wrap;
  margin: 6px 0;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.kindLink {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  color: var(--brand-navy);
  font-family: var(--font-ui);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-decoration: none;
  text-transform: uppercase;
  white-space: nowrap;
}

.kindLink + .kindLink {
  border-left: 1px solid var(--border-control);
}

.kindLink:hover {
  background: var(--gray-50);
}

.kindLink:focus-visible {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -2px;
}

.kindLink[aria-current] {
  background: var(--brand-navy);
  color: var(--white);
}

.kindCount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: var(--radius-pill);
  background: var(--brand-gold);
  color: var(--brand-navy);
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: var(--fw-bold);
  letter-spacing: 0;
}

/* --- X2: the over-budget pool's year (bars, legend, ledger) ------------------------------------------ */

.barSky {
  background: var(--brand-sky);
}

.barCyan {
  background: var(--brand-cyan);
}

/* The awarded bar carries a mark where the budget ends. */
.barMarked {
  position: relative;
}

.barMark {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  margin-left: -1px;
  background: var(--status-problem-ink);
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 12px 0 0;
  padding: 0;
  color: var(--text-secondary);
  font-size: 13px;
  list-style: none;
}

.legend li {
  display: inline-flex;
  gap: 6px;
  align-items: center;
}

.swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.swatchMark {
  width: 2px;
  background: var(--status-problem-ink);
}

.ledgerEntry {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  align-items: center;
}

.positive {
  color: var(--status-done-ink);
}

/* --- X3: a TAA's split for this award, and its pool moves (QA-048) ----------------------------------- */

.splitControl {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--border-hairline);
}

.splitGroup {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.splitField {
  display: flex;
  flex: 1 1 auto;
}

/* A radio drawn as a segment of the board's control: the whole segment is the target. */
.splitOption {
  display: inline-flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  min-height: 44px;
  padding: 0 14px;
  background: var(--white);
  color: var(--brand-navy);
  font-family: var(--font-ui);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
}

.splitOption[data-selected] {
  background: var(--brand-navy);
  color: var(--white);
}

.splitOption[data-focus-visible] {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: -3px;
}

.splitOption[data-disabled] {
  cursor: default;
  opacity: 0.6;
}

.splitCustom {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  align-items: flex-end;
}

.poolMoves {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.poolMovesList {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  color: var(--text-heading);
  font-size: 14px;
  list-style: none;
}

.poolMovesList li {
  padding: 8px 0;
  border-bottom: 1px solid var(--border-hairline);
}

/* --- SL7: Budget impact's bar and options line ---------------------------------------------------- */

.poolBar {
  display: flex;
  flex: 1 1 100%;
  height: 10px;
  overflow: hidden;
  border-radius: var(--radius-sm);
  background: var(--gray-100);
}

.poolOptions {
  display: flex;
  flex: 1 1 100%;
  flex-wrap: wrap;
  gap: 4px 14px;
  justify-content: space-between;
  color: var(--text-secondary);
  font-size: 14px;
}

/* APP-105: Select all ready, what's selected and Mark N checked, above the queue's list. */
.markBar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  align-items: center;
  margin: 0 0 12px;
  padding: 4px 12px;
  border: 1px solid var(--border-hairline);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
}

.markSummary {
  flex: 1 1 240px;
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

/* What the ticks reach, and the way to widen or clear them (APP-135): its own line in the bar. */
.markReach {
  display: flex;
  flex: 1 1 100%;
  flex-wrap: wrap;
  gap: 4px 8px;
  align-items: center;
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  line-height: 1.5;
}

/* The award cell: the row's box beside its number, the same in the table and the phone card. */
.selectAward {
  display: flex;
  gap: 4px;
  align-items: flex-start;
}

/* A row that can't be selected keeps the box's width, so the numbers line up. */
.selectSpacer {
  flex: none;
  width: 32px;
}

.ready {
  color: var(--status-done-ink);
}

/* APP-109: an award's details, opened in place in the queue: its people, then three facts. */
.rowDetails {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
}

.rowPeople {
  align-self: stretch;
  min-width: 0;
  padding: 0 4px;
  border-radius: var(--radius-sm);
  background: var(--white);
}

.rowFacts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
  gap: 12px;
  align-self: stretch;
}

.rowFact {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--white);
}

.rowFact p {
  margin: 0;
}

.rowFact .paidFrom {
  align-self: stretch;
}
